import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Maximize, RefreshCw } from 'lucide-react';
import { API } from '@/utils/api';
import { fmtRange, todayISO } from '@/components/agenda/agendaUtils';
import { PROGRESS, ProgressPill } from '@/components/agenda/progress';

const Y = '#FACC15';
const REFRESH_MS = 60000;

const useClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
};

const Row = ({ it, onChanged }) => {
  const task = it.kind === 'task';
  const color = task ? '#64748B' : it.internal ? '#16A34A' : (PROGRESS[it.progress] || PROGRESS.prevu).color;
  return (
    <div className="grid items-center gap-6 rounded-xl px-6 py-4" data-testid={`tv-row-${it.id}`}
      style={{ gridTemplateColumns: '17% 1fr 1fr auto', background: it.internal ? '#0F2A1A' : '#181C23', borderLeft: `10px solid ${color}` }}>
      <div className="font-black text-[2.1vw] leading-tight" style={{ color: Y }}>{it.ranges?.length > 1 ? it.ranges.map(r => fmtRange(r[0], r[1])).join(' / ') : fmtRange(it.time, it.end_time)}</div>
      <div className="min-w-0">
        <div className={`font-black text-[2.2vw] truncate ${it.internal ? 'text-green-400' : 'text-white'}`}>{it.title}</div>
        <div className="text-[1.4vw] text-slate-400 truncate">{it.subtitle}</div>
      </div>
      <div className="text-[1.7vw] font-bold text-white leading-snug line-clamp-2">{it.work}</div>
      <div className="text-[1.5vw]">{task ? <span className="text-slate-400 font-bold uppercase">Atelier</span> : <ProgressPill item={it} big onChanged={onChanged} />}</div>
    </div>
  );
};

export default function AgendaTvPage({ atelier = false }) {
  const navigate = useNavigate();
  const now = useClock();
  const [data, setData] = useState(null);
  const [updated, setUpdated] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/agenda/day-planning`, { params: { date: todayISO() } })
      .then(r => { setData(r.data); setUpdated(new Date()); }).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);

  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {});
  const items = data?.items || [];
  const counts = Object.keys(PROGRESS).map(k => [k, items.filter(i => i.kind === 'appointment' && (i.progress || 'prevu') === k).length]);

  return (
    <div className="min-h-screen bg-[#0E1116] text-white p-[2vw] flex flex-col gap-[1.5vw]" data-testid="agenda-tv-page">
      <header className="flex items-end justify-between border-b border-[#2A303A] pb-[1vw]">
        <div>
          <div className="text-[1.3vw] font-extrabold tracking-[0.1em] uppercase" style={{ color: Y }}>{data?.company_name || 'Atelier'}</div>
          <h1 className="text-[3.4vw] font-black leading-none">Planning atelier</h1>
        </div>
        <div className="ml-auto mr-8 flex gap-2 self-start">
          <button onClick={() => navigate(atelier ? '/atelier/planning' : '/admin/agenda')} className="opacity-40 hover:opacity-100 transition-opacity p-2" data-testid="tv-back-btn" title="Retour à l'agenda"><ArrowLeft className="w-6 h-6" /></button>
          <button onClick={load} className="opacity-40 hover:opacity-100 transition-opacity p-2" data-testid="tv-refresh-btn" title="Actualiser"><RefreshCw className="w-6 h-6" /></button>
          <button onClick={fullscreen} className="opacity-40 hover:opacity-100 transition-opacity p-2" data-testid="tv-fullscreen-btn" title="Plein écran"><Maximize className="w-6 h-6" /></button>
        </div>
        <div className="text-right">
          <div className="text-[1.5vw] font-extrabold uppercase capitalize" style={{ color: Y }}>{now.toLocaleDateString('fr-CH', { weekday: 'long', day: '2-digit', month: 'long' })}</div>
          <div className="text-[4vw] font-black leading-none tabular-nums" data-testid="tv-clock">{now.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      </header>
      <div className="flex gap-3 flex-wrap" data-testid="tv-counters">
        {counts.map(([k, n]) => (
          <div key={k} className="flex items-center gap-2 rounded-full px-4 py-1.5 bg-[#181C23] text-[1.2vw] font-bold" data-testid={`tv-count-${k}`}>
            <span className="w-3 h-3 rounded-full" style={{ background: PROGRESS[k].color }} />{PROGRESS[k].label} <span style={{ color: Y }}>{n}</span>
          </div>
        ))}
      </div>
      <main className="flex-1 flex flex-col gap-[0.8vw] overflow-hidden">
        {!data && <p className="text-[2vw] text-slate-400">Chargement…</p>}
        {data && items.length === 0 && <p className="text-[2vw] text-slate-400" data-testid="tv-empty">Aucune intervention prévue aujourd'hui</p>}
        {items.map(it => <Row key={it.id} it={it} onChanged={load} />)}
      </main>
      <footer className="flex justify-between text-[1vw] text-slate-500 font-bold uppercase tracking-wider">
        <span>Équipe : {(data?.team || []).map(m => m.name).join(' · ') || '—'}</span>
        <span data-testid="tv-updated">Mis à jour {updated ? updated.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }) : '—'} · auto chaque minute</span>
      </footer>
    </div>
  );
}
